// import { Combobox, ComboboxOption } from '../../fieldsComponents/appointments/combobox';
import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Field, Label } from '../../fieldsComponents/appointments/fieldset';
import { Combobox, ComboboxOption } from '../../fieldsComponents/appointments/combobox';

const DialogRemainderEdit = ({ isOpen, onClose, onSave, initialRemainderData }) => {
  const [type, setType] = useState(initialRemainderData?.type || 'email');
  const [remainderType, setRemainderType] = useState(initialRemainderData?.remainderType || 'days');
  const [remainderValue, setRemainderValue] = useState(initialRemainderData?.remainderValue || '');
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);

  // Update state if initialRemainderData changes
  useEffect(() => {
    setType(initialRemainderData?.type || 'email');
    setRemainderType(initialRemainderData?.remainderType || 'days');
    setRemainderValue(initialRemainderData?.remainderValue || '');
  }, [initialRemainderData]);

  useEffect(() => {
    setIsSaveEnabled(remainderValue.trim() !== '');
  }, [remainderValue]);

  const handleSave = () => {
    const remainderData = { type, remainderType, remainderValue };
    console.log("Saving remainder data:", remainderData); // Debug logging
    onSave(remainderData);
  };

  const handleRemainderTypeChange = (e) => {
    setRemainderType(e.target.value);
  };

  const handleRemainderValueChange = (e) => {
    setRemainderValue(e.target.value);
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => { }} // Empty function to prevent auto-closing
        static // Add static prop to prevent closing on outside click
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-4">
                <div className="relative p-0">
                  <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-1 right-1 p-1 rounded-full bg-black hover:bg-gray-800 transition-colors duration-200 text-white hover:text-gray-200 z-10"
                    aria-label="Close"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Header with border-bottom */}
                <div className="border-b border-gray-200 py-4 mb-8">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Remainder
                  </Dialog.Title>
                </div>

                {/* Main content */}
                <div className="mt-8 px-4">
                  <div className="flex flex-col sm:flex-row items-center mb-4">
                    <Field className="flex items-center w-full">
                      <Label className="block text-sm font-medium text-gray-900 w-1/3">Type</Label>
                      <div className="flex items-center">
                        <div className="w-[180px] sm:w-[240px]">
                          <Combobox
                            options={[
                              { id: 'email', name: 'Email' },
                              { id: 'popup', name: 'Popup' }
                            ]}
                            value={[{ id: 'email', name: 'Email' }, { id: 'popup', name: 'Popup' }].find(opt => opt.id === type) || null}
                            displayValue={opt => (opt && opt.name ? opt.name : '')}
                            onChange={opt => {
                              if (opt && opt.id) {
                                setType(opt.id);
                              }
                            }}
                            filter={(opt, query) => opt.name.toLowerCase().includes(query.toLowerCase())}
                            dropdownClassName="min-w-[180px] sm:w-[240px] bg-white z-50"
                            anchor="bottom"
                          >
                            {option => (
                              <ComboboxOption key={option.id} value={option} className="border-none outline-none focus:ring-0">
                                {option.name}
                              </ComboboxOption>
                            )}
                          </Combobox>
                        </div>
                      </div>
                    </Field>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center mb-4">
                    <Field className="flex items-center w-full">
                      <Label className="block text-sm font-medium text-gray-900 w-1/3">Remainder</Label>
                      <div className="flex-grow flex flex-row items-center space-x-2">
                        <input
                          type="number"
                          value={remainderValue}
                          onChange={handleRemainderValueChange}
                          className="block w-[80px] rounded-md border-0 h-8 px-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-black text-xs"
                        />
                        <div className="w-1/2">
                          <Combobox
                            options={[{ id: 'days', name: 'Days' }, { id: 'hours', name: 'Hours' }, { id: 'minutes', name: 'Minutes' }]}
                            value={[{ id: 'days', name: 'Days' }, { id: 'hours', name: 'Hours' }, { id: 'minutes', name: 'Minutes' }].find(opt => opt.id === remainderType) || null}
                            displayValue={opt => (opt && opt.name ? opt.name : '')}
                            onChange={opt => {
                              if (opt && opt.id) {
                                setRemainderType(opt.id);
                              }
                            }}
                            filter={(opt, query) => opt.name.toLowerCase().includes(query.toLowerCase())}
                            dropdownClassName="min-w-[160px] max-w-[220px] bg-white z-50"
                            anchor="bottom"
                          >
                            {option => (
                              <ComboboxOption key={option.id} value={option} className="border-none outline-none focus:ring-0">
                                {option.name}
                              </ComboboxOption>
                            )}
                          </Combobox>
                        </div>
                      </div>
                    </Field>
                  </div>
                </div>

                {/* Footer section with border */}
                <div className="border-t border-gray-200 mt-10">
                  <div className="flex justify-between mt-5">
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-md bg-gray-200 text-gray-700 py-2 px-4 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={!isSaveEnabled}
                      className={`rounded-md ${!isSaveEnabled ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-800"} text-white py-2 px-4 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2`}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DialogRemainderEdit;
